import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import GameListImage from "@/components/game-list-image";
import IconStack, { IconsEnum } from "@/components/icon-stack";
import {
  DislikeIcon,
  LikeIcon,
  LoveIcon,
} from "@/components/icons/big-rating-icons";
import { gameUrl } from "@/helpers/games";
import { cn } from "@/lib/utils";
import { OverallRatingEnum } from "@/pages/search/types";
import type { GameType } from "../types";

interface GameProps {
  game: GameType;
  isFirst: boolean;
  showRating: boolean;
}

export default function Game({ game, isFirst, showRating }: GameProps) {
  const icons = useMemo(() => {
    if (!showRating) {
      return (
        <div className="relative flex gap-[5px]">
          <IconStack game={game} icons={[IconsEnum.SCORE]} />
        </div>
      );
    }

    if (!game.userReview) {
      return null;
    }

    const { overallRating } = game.userReview;

    if (overallRating === OverallRatingEnum.DISLIKE) {
      return <DislikeIcon />;
    }
    if (overallRating === OverallRatingEnum.LIKE) {
      return <LikeIcon />;
    }
    if (overallRating === OverallRatingEnum.LOVE) {
      return <LoveIcon />;
    }

    return null;
  }, [game, showRating]);

  return (
    <article
      className={cn(
        "relative flex items-center gap-2.5 border border-transparent py-2.5 transition-[colors,border-radius] md:px-2.5",
        "hover:rounded-[5px] hover:border-white/10 hover:bg-white/5",
        !isFirst && "border-t-white/10"
      )}
    >
      <GameListImage
        className="size-10 shrink-0 md:size-15"
        game={game}
        withoutActions
      >
        <div className="absolute -top-1.5 left-1.5 md:-top-px md:left-2.5">
          <IconStack
            game={game}
            iconClassName="h-4 md:h-6 w-[11px] md:w-4"
            icons={[IconsEnum.AWARDS]}
          />
        </div>
      </GameListImage>
      <div className="flex grow-1 flex-col items-stretch md:gap-[3px]">
        <h2 className="font-medium text-13 text-white leading-[1.5em] md:text-16">
          <Link className="link-overlay" to={gameUrl(game)}>
            {game.name}
          </Link>
        </h2>
        <h3 className="font-normal text-12 text-white/80 leading-[1.5em] md:text-pale-silver">
          {game.location.displayName || game.location.company.name}
        </h3>
      </div>
      {icons}
    </article>
  );
}
